import { z } from "zod";
import { GUIDE_VERSION, PRESENTATION_LAYOUTS, SOCIAL_TARGETS } from "./video-presentation.js";
import { SOCIAL_LAYOUTS, SOCIAL_APPEARANCE_LIMITS } from './social-presentation.js';
import { PLATE_TRANSPARENCY_LIMITS } from './presentation-appearance.js';

const plateTransparency = z.number().int().min(PLATE_TRANSPARENCY_LIMITS.min).max(PLATE_TRANSPARENCY_LIMITS.max);
const textColor = z.string().regex(/^#[0-9a-f]{6}$/i).transform(value => value.toLowerCase());
export const PresentationAppearanceSchema = z.object({ plateTransparency: plateTransparency.optional(), textColor: textColor.optional() }).strict();

/** Strict settings accepted by every render admission boundary. */
export const LegacyVideoPresentationSchema = z.object({
  version: z.literal(1), guideVersion: z.literal(GUIDE_VERSION),
  target: z.enum(["none", ...SOCIAL_TARGETS]), layout: z.enum(PRESENTATION_LAYOUTS),
  titlePosition: z.enum(["upper", "above-statistics", "hidden"]),
  titleScale: z.number().int().min(80).max(110), statisticsScale: z.number().int().min(80).max(110),
  statisticsLayout: z.enum(["four-columns", "two-by-two"]),
  showTotals: z.boolean(), showElevation: z.boolean(), showRideDetails: z.boolean(),
  plateTransparency: plateTransparency.optional(), textColor: textColor.optional(),
}).strict().refine(value => value.target !== "none" || value.layout === "original", "Choose a social placement for this layout.");

export const SocialVideoPresentationSchema = z.object({
  version: z.literal(2), guideVersion: z.literal(GUIDE_VERSION), target: z.enum(['none', ...SOCIAL_TARGETS]), layout: z.enum(SOCIAL_LAYOUTS),
  titleSize: z.number().int().min(SOCIAL_APPEARANCE_LIMITS.titleSize.min).max(SOCIAL_APPEARANCE_LIMITS.titleSize.max),
  statisticsSize: z.number().int().min(SOCIAL_APPEARANCE_LIMITS.statisticsSize.min).max(SOCIAL_APPEARANCE_LIMITS.statisticsSize.max),
  extendedNumeralScale: z.number().int().min(SOCIAL_APPEARANCE_LIMITS.extendedNumeralScale.min).max(SOCIAL_APPEARANCE_LIMITS.extendedNumeralScale.max),
  plateTransparency,
  upperVerticalOffset: z.number().int().min(SOCIAL_APPEARANCE_LIMITS.upperVerticalOffset.min).max(SOCIAL_APPEARANCE_LIMITS.upperVerticalOffset.max)
    .default(SOCIAL_APPEARANCE_LIMITS.upperVerticalOffset.default),
  lowerVerticalOffset: z.number().int().min(SOCIAL_APPEARANCE_LIMITS.lowerVerticalOffset.min).max(SOCIAL_APPEARANCE_LIMITS.lowerVerticalOffset.max)
    .default(SOCIAL_APPEARANCE_LIMITS.lowerVerticalOffset.default),
  textColor,
  showTotals: z.boolean(), showElevation: z.boolean(),
}).strict();
export const VideoPresentationSchema = z.union([LegacyVideoPresentationSchema, SocialVideoPresentationSchema]);

/** D1 legacy rows have no presentation column value. Invalid saved settings fail visibly. */
export function readStoredPresentation(value: string | null | undefined) {
  return value == null ? undefined : VideoPresentationSchema.parse(JSON.parse(value));
}
